import { useState, useEffect, useRef, useCallback } from 'preact/hooks';
import { useOddball } from '../midi-context';
import type { GestureEvent } from '../../src/types';

const TYPE_COLORS: Record<string, string> = {
  rollStart: '#4a7',
  rolling: '#4a7',
  rollDirectionChange: '#7a4',
  rollEnd: '#4a7',
  throwStart: '#a74',
  airborne: '#a74',
  caught: '#47a',
  landed: '#a44',
  abruptStop: '#a47',
  sustainedShake: '#508cff',
  sustainedTwist: '#b450ff',
  idle: '#555',
};

function formatEvent(evt: GestureEvent): string {
  switch (evt.type) {
    case 'rollStart': return 'Roll started';
    case 'rolling': return `Rolling ${(evt.elapsed / 1000).toFixed(1)}s spin=${evt.spinSpeed} heading=${evt.heading} freq=${evt.oscillationFreq.toFixed(1)}Hz`;
    case 'rollDirectionChange': return `Direction: ${evt.previousHeading} → ${evt.newHeading}`;
    case 'rollEnd': return `Roll ended ${(evt.duration / 1000).toFixed(1)}s avgSpin=${evt.averageSpinSpeed.toFixed(0)} dirs=${evt.directionChanges} avgFreq=${evt.averageOscillationFreq.toFixed(1)}Hz`;
    case 'throwStart': return 'Throw!';
    case 'airborne': return `Airborne ${(evt.elapsed / 1000).toFixed(1)}s ff=${evt.freeFallIntensity} spin=${evt.spinSpeed}`;
    case 'caught': return `Caught! ${evt.airborneMs}ms vel=${evt.catchVelocity}`;
    case 'landed': return `Landed ${evt.airborneMs}ms`;
    case 'abruptStop': return `Abrupt stop energy=${evt.priorEnergyLevel} ${evt.motionDuration}ms`;
    case 'sustainedShake': return `Sustained shake x${evt.burstCount} peak=${evt.peakIntensity}`;
    case 'sustainedTwist': return `Sustained twist x${evt.eventCount} peak=${evt.peakIntensity}`;
    case 'idle': return `Idle ${(evt.idleDuration / 1000).toFixed(1)}s`;
  }
}

export function GestureLog() {
  const { detector } = useOddball();
  const [events, setEvents] = useState<GestureEvent[]>([]);
  const [paused, setPaused] = useState(false);
  const pausedRef = useRef(false);

  useEffect(() => { pausedRef.current = paused; }, [paused]);

  useEffect(() => {
    const handler = (evt: GestureEvent) => {
      if (pausedRef.current) return;
      setEvents((prev) => [evt, ...prev].slice(0, 200));
    };
    detector.on('gesture', handler);
    return () => { detector.off('gesture', handler); };
  }, [detector]);

  const clear = useCallback(() => setEvents([]), []);

  return (
    <div>
      <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '12px' }}>
        <h2 style={{ fontSize: '1rem', fontWeight: 300, color: '#888' }}>Gesture Log</h2>
        <button
          onClick={() => setPaused(!paused)}
          style={{
            padding: '3px 10px', borderRadius: '10px', border: '1px solid #444',
            background: paused ? '#442' : 'transparent', color: '#aaa',
            cursor: 'pointer', fontSize: '0.7rem',
          }}
        >
          {paused ? 'Resume' : 'Pause'}
        </button>
        <button
          onClick={clear}
          style={{
            padding: '3px 10px', borderRadius: '10px', border: '1px solid #444',
            background: 'transparent', color: '#aaa', cursor: 'pointer', fontSize: '0.7rem',
          }}
        >
          Clear
        </button>
      </div>

      <div style={{ fontSize: '0.65rem', color: '#444', marginBottom: '8px', lineHeight: 1.6, maxWidth: '500px' }}>
        Future: a "learn mode" could record motion patterns for later analysis, enabling recognition
        of complex gestures like rocking back-and-forth, figure-eights, or cradle motions — regardless
        of absolute orientation. Pattern matching on recorded CC sequences would unlock far richer interaction.
      </div>

      <div style={{ display: 'flex', flexDirection: 'column', gap: '3px', fontFamily: 'monospace', fontSize: '0.75rem' }}>
        {events.length === 0 && (
          <div style={{ color: '#444', padding: '20px 0' }}>Waiting for gestures...</div>
        )}
        {events.map((evt, i) => (
          <div key={i} style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
            <span
              style={{
                padding: '2px 8px',
                borderRadius: '8px',
                background: TYPE_COLORS[evt.type] ?? '#555',
                color: '#111',
                fontSize: '0.65rem',
                fontWeight: 600,
                minWidth: '80px',
                textAlign: 'center',
                whiteSpace: 'nowrap',
              }}
            >
              {evt.type}
            </span>
            <span style={{ color: '#888' }}>{formatEvent(evt)}</span>
          </div>
        ))}
      </div>
    </div>
  );
}
